/** Project overview (index child of the app shell): name, description, repository, wiki template, compile mode, Source and wiki page counts */

import { Link, useParams } from 'react-router-dom'
import { BookOpen, ExternalLink, Library } from 'lucide-react'
import { useProject } from '../hooks/use-projects'
import { useCompileMode } from '../hooks/use-compile-mode'
import { useAppStore } from '../stores/app-store'
import { useT } from '../i18n'
import { projectPath } from '../components/shell/nav-config'

/** Only render http(s) repository URLs as links (repoUrl is free text on the API side) */
function safeHttpUrl(value: string | null): string | null {
  if (!value) return null
  try {
    const url = new URL(value)
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.toString() : null
  } catch {
    return null
  }
}

export function ProjectOverviewPage() {
  const t = useT()
  const locale = useAppStore((s) => s.locale)
  const { projectSlug } = useParams<{ projectSlug: string }>()
  const { data: project, isLoading, error, refetch } = useProject(projectSlug)
  const compileMode = useCompileMode()

  if (isLoading) {
    return <p className="px-4 py-8 text-sm text-fg-subtle md:px-8">{t('common.loading')}</p>
  }

  if (error || !project) {
    const notFound = error?.status === 404
    return (
      <div className="space-y-3 px-4 py-8 md:px-8" role="alert">
        <p className="text-sm text-fg">
          {notFound ? t('project.overview.notFound', { slug: projectSlug ?? '' }) : t('project.overview.loadError')}
        </p>
        {notFound ? (
          <Link to={projectPath('default')} className="text-sm font-medium text-accent-text underline underline-offset-2">
            {t('project.overview.backToProjects')}
          </Link>
        ) : (
          <button type="button" onClick={() => void refetch()} className="cursor-pointer text-sm font-medium text-accent-text underline underline-offset-2">
            {t('common.retry')}
          </button>
        )}
      </div>
    )
  }

  const repoHref = safeHttpUrl(project.repoUrl)
  const updated = new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(project.updatedAt))
  const stats = [
    { labelKey: 'project.overview.sources' as const, value: project.sourceCount, to: projectPath(project.slug, 'sources'), icon: Library },
    { labelKey: 'project.overview.pages' as const, value: project.pageCount, to: projectPath(project.slug, 'wiki'), icon: BookOpen },
  ]

  return (
    <div className="mx-auto w-full max-w-4xl space-y-8 px-4 py-6 md:px-8 md:py-10">
      <header className="space-y-3">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <h1 className="min-w-0 break-words font-display text-2xl font-semibold tracking-tight text-fg md:text-3xl">{project.name}</h1>
          {project.isDefault && (
            <span className="rounded-md border border-border px-2 py-0.5 text-xs text-fg-subtle">{t('project.overview.defaultProject')}</span>
          )}
        </div>
        <p className="max-w-2xl whitespace-pre-line break-words text-sm leading-relaxed text-fg-muted md:text-base">
          {project.description || t('project.overview.noDescription')}
        </p>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-fg-subtle">
          <span>{t('project.overview.updated', { date: updated })}</span>
          <span>{t('wikiTemplate.label')}: {t(`wikiTemplate.${project.wikiTemplate ?? 'custom'}`)}</span>
          <Link to="/settings/preferences" className="hover:text-fg hover:underline">
            {t('compileMode.label')}: {t(`compileMode.${compileMode}`)}
          </Link>
          {project.repoUrl && (
            <span className="flex min-w-0 items-center gap-1.5">
              <span>{t('project.overview.repository')}:</span>
              {repoHref ? (
                <a href={repoHref} target="_blank" rel="noopener noreferrer" className="inline-flex min-w-0 items-center gap-1 text-accent-text hover:underline">
                  <span className="truncate">{project.repoUrl}</span>
                  <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                </a>
              ) : (
                <span className="truncate">{project.repoUrl}</span>
              )}
            </span>
          )}
        </div>
      </header>

      <section className="grid gap-4 sm:grid-cols-2">
        {stats.map(({ labelKey, value, to, icon: Icon }) => (
          <Link
            key={labelKey}
            to={to}
            className="group rounded-xl border border-border bg-surface-raised p-5 hover:border-border-strong"
          >
            <div className="flex items-center justify-between text-fg-muted">
              <span className="text-sm font-medium">{t(labelKey)}</span>
              <Icon className="h-4 w-4 text-accent-text" aria-hidden="true" />
            </div>
            <p className="mt-3 font-display text-3xl font-semibold tabular-nums text-fg">{value.toLocaleString(locale)}</p>
            <p className="mt-1 text-sm text-fg-subtle group-hover:text-accent-text">{t('project.overview.open')} →</p>
          </Link>
        ))}
      </section>
    </div>
  )
}
